import SingleBanner from "@/components/carousel/SingleBanner";
import Image from "next/legacy/image";
import SocialShare from "@/components/sections/SocialShare";
import BlogList from "@/components/sections/BlogList";
import { getBlogListData } from "@/api/server/blog";
import { getSafeStaticProps, orNull } from "@/utils/helpers/isr.helpers";
import { useRouter } from "next/router";
import MetaData from "@/components/seo/MetaData";
import { BlogRightSection } from "@/components/sections/BlogRight";
import Card from "@/components/product-card/large/Card";
import dynamic from "next/dynamic";
const AddToCartModal = dynamic(
  () => import("@/components/modal/AddToCartModal"),
  {
    ssr: false,
  }
);

function Blog({
  blogData,
  relatedBlog,
  metadata,
  schema,
  relatedProducts,
  topSellingProducts,
  latestArticles,
}) {
  const router = useRouter();
  const MAX_BLOG_TO_SHOW = 3;
  const BLOG_URL = `${process.env.NEXT_PUBLIC_SITE_ABSOLUTE_URL}${router.asPath}`;

  return (
    <>
      <MetaData title={metadata.name} {...metadata} schema={schema} />

      <div className="mx-auto ">
        <div className="px-[1.25rem] lg:px-[7.5rem] pt-12 pb-24">
          <div className="block lg:hidden">
            <SingleBanner
              className="aspect-[16/9]"
              imageOptions={{ className: "rounded-xl" }}
              bgImageUrl={blogData?.thumbnail || blogData?.banner}
              alt={blogData?.title}
            />
          </div>

          <div className="hidden lg:block">
            <SingleBanner
              className="lg:aspect-[60/19]"
              imageOptions={{ className: "rounded-xl" }}
              bgImageUrl={blogData?.banner}
              alt={blogData?.title}
            />
          </div>

          <div className="flex flex-col lg:flex-row lg:justify-between pt-8 lg:pt-[3.75rem]">
            <div className="lg:w-[66%]">
              <div className="w-full grid grid-cols-12">
                <div className="hidden lg:block col-span-3"></div>
                <div className="col-span-12">
                  <h1 className="text-2xl lg:text-display2 font-bold text-grayD2">
                    {blogData?.title}
                  </h1>
                </div>
                <div className="hidden lg:block col-span-3"></div>
              </div>

              <div className="w-full grid grid-cols-12 mt-9">
                <div className="col-span-12">
                  <p className="font-raleway font-medium  text-base lg:text-heading4 text-grayD3">
                    {blogData?.short_description}
                  </p>

                  <div className="flex items-center justify-between mt-11">
                    <div className="flex items-center space-x-3">
                      <Image
                        src={blogData?.authors[0]?.profile_pic}
                        width={40}
                        height={40}
                        alt="user-profile"
                        className="rounded-full"
                      />
                      <div className="space-y-1">
                        <p className="text-grayD2">
                          {blogData?.authors[0]?.name}
                        </p>
                        <p>{blogData?.published_date}</p>
                      </div>
                    </div>
                    <SocialShare
                      shareAbleLink={BLOG_URL}
                      title={blogData?.title}
                    />
                  </div>
                </div>
                <div className="hidden lg:col-span-3"></div>
              </div>

              <div className="w-full grid grid-cols-12 mt-12">
                <div className="hidden lg:block col-span-3"></div>
                <div className="col-span-12 admin-text-editor">
                  <div
                    dangerouslySetInnerHTML={{ __html: blogData.description }}
                  />
                </div>
                <div className="hidden lg:block col-span-3"></div>
              </div>
            </div>
            <div className="lg:w-[25.5%] lg:max-w-[306px] break-all">
              <BlogRightSection
                latestArticles={latestArticles}
                topSellingProducts={topSellingProducts}
              />
            </div>
          </div>

          {relatedProducts.length > 0 && (
            <>
              <div className="my-[60px] lg:my-[100px]">
                <div className="w-full mb-[0.5rem] mt-[1rem] border border-grayD8" />
              </div>

              <div>
                <h2 className="font-semibold lg:font-medium text-grayD2 text-2xl lg:text-5xl mb-[56px] lg:mb-[80px]">
                  Related Products
                </h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-[1.5rem]">
                  {relatedProducts.slice(0, 4).map((product) => (
                    <Card
                      product={{ ...product, variations: [] }}
                      key={product.slug}
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          {relatedBlog.length > 0 && (
            <>
              <div className="my-[60px] lg:my-[100px]">
                <div className="w-full mb-[0.5rem] mt-[1rem] border border-grayD8" />
              </div>

              <div>
                <h2 className="font-semibold lg:font-medium text-grayD2 text-2xl lg:text-5xl mb-[56px] lg:mb-[80px] text-center">
                  More Blog Articles
                </h2>
                <BlogList blogData={relatedBlog?.slice(0, MAX_BLOG_TO_SHOW)} />
              </div>
            </>
          )}

          <AddToCartModal />
        </div>
      </div>
    </>
  );
}

export const getStaticPaths = async () => {
  const resData = await getBlogListData();
  const articles = resData?.data?.articles ?? [];

  // If the list API fails, pages are generated on first request (blocking).
  const paths = articles.map(({ slug }) => ({ params: { slug } }));

  return { paths, fallback: "blocking" };
};

export const getStaticProps = async ({ params }) => {
  return getSafeStaticProps({
    url: `/v1/articles/${params.slug}`,
    mapToProps: ({ data }) => {
      if (!data?.article) return null;
      return {
        blogData: data.article,
        relatedBlog: data.related_articles ?? [],
        metadata: data.metadata ?? {},
        schema: orNull(data.schema),
        relatedProducts: data.related_products ?? [],
        latestArticles: data.latest_articles ?? [],
        topSellingProducts: data.top_selling_products ?? [],
      };
    },
    // No fallbackProps: this page can't render without data, so on a build
    // failure it is regenerated on the next request instead.
  });
};

export default Blog;
